import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api, formatApiError, API } from "../lib/api";
import { useAuth } from "../auth/AuthContext";
import { useI18n } from "../i18n/I18nContext";
import DashboardLayout from "../components/DashboardLayout";
import { GraduationCap, Upload, FolderOpen, FileText, Download, Check, Calendar, Briefcase, Rocket, Youtube } from "lucide-react";
import { toast } from "sonner";
import { formatMondayDate, nextWeek, daysUntil } from "../lib/date";

export default function StudentDashboard() {
  const { user } = useAuth();
  const { t, lang } = useI18n();
  const nav = useNavigate();

  const [enrollment, setEnrollment] = useState(null);
  const [assignments, setAssignments] = useState([]);
  const [submissions, setSubmissions] = useState([]);
  const [resources, setResources] = useState([]);
  const [serviceOrders, setServiceOrders] = useState([]);
  const [busyId, setBusyId] = useState(null);
  const [section, setSection] = useState("progress");

  useEffect(() => {
    if (!window.location.hash) window.location.hash = "#progress";
    const onHash = () => setSection((window.location.hash || "#progress").replace("#", ""));
    onHash();
    window.addEventListener("hashchange", onHash);
    return () => window.removeEventListener("hashchange", onHash);
  }, []);

  const reload = async () => {
    try {
      const [a, b, c, d, e] = await Promise.all([
        api.get("/me/enrollment"),
        api.get("/assignments"),
        api.get("/submissions/mine"),
        api.get("/resources"),
        api.get("/me/services"),
      ]);
      setEnrollment(a.data && Object.keys(a.data).length ? a.data : null);
      setAssignments(b.data || []);
      setSubmissions(c.data || []);
      setResources(d.data || []);
      setServiceOrders(e.data || []);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || e.message); }
  };

  useEffect(() => { reload(); }, []);

  const navItems = [
    { id: "progress", hash: "#progress", icon: GraduationCap, label: t("dashboard.progress") },
    { id: "homework", hash: "#homework", icon: Upload, label: t("dashboard.homework") },
    { id: "vault", hash: "#vault", icon: FolderOpen, label: t("dashboard.vault") },
    ...(serviceOrders.length ? [
      { id: "onboarding", hash: "#onboarding", icon: Rocket, label: t("services.onboarding.tab") },
      { id: "project", hash: "#project", icon: Briefcase, label: t("services.project.title") },
    ] : []),
  ];

  const submittedFor = (aid) => submissions.find((s) => s.assignment_id === aid);
  const videosForWeek = (w) => resources.filter((r) => r.kind === "video" && r.week === w);
  const upcoming = nextWeek(assignments);
  const dLeft = upcoming ? daysUntil(upcoming.live_date) : null;

  const onSubmit = async (assignmentId, file) => {
    if (!file) return;
    setBusyId(assignmentId);
    try {
      const fd = new FormData();
      fd.append("assignment_id", assignmentId);
      fd.append("file", file);
      await api.post("/submissions", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Submitted!");
      reload();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    } finally { setBusyId(null); }
  };

  const downloadUrl = (path) => `${API}/files/download?path=${encodeURIComponent(path)}`;

  return (
    <DashboardLayout title={`${t("dashboard.welcome")}, ${user?.name?.split(" ")[0] || ""}`} navItems={navItems}>
      {/* Next live session banner */}
      {upcoming && (
        <div className="card-imperial p-5 mb-6 flex flex-col md:flex-row md:items-center gap-4 border-[#d4af37]/30" data-testid="next-session-banner">
          <div className="h-12 w-12 bg-[#d4af37]/10 border border-[#d4af37]/40 flex items-center justify-center flex-shrink-0">
            <Calendar size={20} className="text-[#d4af37]" />
          </div>
          <div className="flex-1">
            <div className="overline">{t("format.nextSession")}</div>
            <div className="font-serif text-xl mt-0.5">
              {(upcoming.titles && upcoming.titles[lang]) || upcoming.title}
            </div>
            <div className="text-sm text-neutral-400 mt-1">
              {formatMondayDate(upcoming.live_date, lang)} · 7:30 PM
              {dLeft >= 0 && <span className="text-[#d4af37] ml-2">· {dLeft === 0 ? t("dashboard.today") : `${dLeft} ${dLeft === 1 ? t("countdown.days").replace(/s$/i, "") : t("countdown.days")}`}</span>}
            </div>
          </div>
        </div>
      )}

      {/* Status banner */}
      <div className="card-imperial p-5 mb-8 flex items-center justify-between" data-testid="enrollment-banner">
        <div>
          <div className="overline mb-1">Status</div>
          <div className="font-serif text-2xl">
            {!enrollment && t("dashboard.noEnrollment")}
            {enrollment && enrollment.payment_status === "paid" && <span className="text-[#d4af37]">✓ {t("dashboard.paid")}</span>}
            {enrollment && enrollment.payment_status === "pending_review" && <span className="text-yellow-400">⌛ {t("dashboard.pendingApproval")}</span>}
            {enrollment && enrollment.payment_status === "rejected" && <span className="text-[#e6192b]">✗ Rejected</span>}
          </div>
        </div>
        {!enrollment && (
          <button onClick={() => nav("/enroll")} data-testid="dash-enroll-cta" className="btn-imperial">{t("dashboard.enrollNow")}</button>
        )}
      </div>

      {section === "progress" && (
        <section data-testid="section-progress">
          <div className="overline mb-3">{t("dashboard.progressSub")}</div>
          <h2 className="font-serif text-3xl mb-6">{t("dashboard.progress")}</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {assignments.map((a) => {
              const s = submittedFor(a.id);
              const localizedTitle = (a.titles && a.titles[lang]) || a.title;
              const vids = videosForWeek(a.week);
              return (
                <div key={a.id} className="card-imperial p-5" data-testid={`progress-week-${a.week}`}>
                  <div className="font-serif text-lg leading-tight">{localizedTitle}</div>
                  <div className="mt-2 text-[11px] text-[#d4af37]/80 font-condensed tracking-widest uppercase flex items-center gap-1">
                    <Calendar size={10} /> {t("format.liveDay")} · 7:30 PM
                  </div>
                  {s ? (
                    <div className="mt-3 inline-flex items-center gap-1 text-xs text-[#d4af37]">
                      <Check size={12} /> {t("dashboard.submitted")}{s.grade ? ` · ${s.grade}` : ""}
                    </div>
                  ) : (
                    <div className="mt-3 text-xs text-neutral-500">— pending —</div>
                  )}
                  {vids.length > 0 && (
                    <div className="mt-3 border-t border-[#1a1a1a] pt-3 space-y-1.5" data-testid={`week-videos-${a.week}`}>
                      {vids.map((v) => (
                        <a
                          key={v.id}
                          href={v.youtube_url}
                          target="_blank"
                          rel="noreferrer"
                          className="flex items-center gap-2 text-xs text-neutral-300 hover:text-[#d4af37] transition-colors"
                          data-testid={`video-link-${v.id}`}
                        >
                          <Youtube size={12} className="text-[#e6192b] shrink-0" />
                          <span className="truncate">{v.title}</span>
                        </a>
                      ))}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </section>
      )}

      {section === "homework" && (
        <section data-testid="section-homework">
          <div className="overline mb-3">{t("dashboard.homeworkSub")}</div>
          <h2 className="font-serif text-3xl mb-6">{t("dashboard.homework")}</h2>
          <div className="space-y-4">
            {assignments.map((a) => {
              const s = submittedFor(a.id);
              const localizedTitle = (a.titles && a.titles[lang]) || a.title;
              const localizedDesc = (a.descriptions && a.descriptions[lang]) || a.description;
              return (
                <div key={a.id} className="card-imperial p-5 flex flex-col md:flex-row md:items-center gap-4" data-testid={`hw-row-${a.week}`}>
                  <div className="flex-1">
                    <div className="overline">{t("format.liveDay")} · 7:30 PM</div>
                    <div className="font-serif text-xl">{localizedTitle}</div>
                    {localizedDesc && <div className="text-sm text-neutral-500 mt-1">{localizedDesc}</div>}
                    {s && s.feedback && (
                      <div className="mt-3 border-l-2 border-[#d4af37] pl-3 text-sm text-neutral-300">
                        <span className="overline mr-2">{t("dashboard.feedback")}</span>{s.feedback}
                      </div>
                    )}
                  </div>
                  <div className="flex items-center gap-3">
                    {s ? (
                      <div className="text-sm text-[#d4af37]">{t("dashboard.submitted")} · {s.grade || "—"}</div>
                    ) : (
                      <label className="btn-outline-gold cursor-pointer">
                        <Upload size={14} /> {busyId === a.id ? t("common.loading") : t("dashboard.submit")}
                        <input
                          type="file"
                          className="hidden"
                          data-testid={`hw-input-${a.week}`}
                          onChange={(e) => onSubmit(a.id, e.target.files?.[0])}
                        />
                      </label>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </section>
      )}

      {section === "vault" && (
        <section data-testid="section-vault">
          <div className="overline mb-3">{t("dashboard.vaultSub")}</div>
          <h2 className="font-serif text-3xl mb-6">{t("dashboard.vault")}</h2>
          {resources.length === 0 ? (
            <div className="text-neutral-500 text-sm">{t("dashboard.noResources")}</div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {resources.map((r) => (
                <div key={r.id} className="card-imperial p-5" data-testid={`resource-${r.id}`}>
                  <div className="flex items-start gap-3">
                    <FileText size={24} className="text-[#d4af37]" />
                    <div className="flex-1 min-w-0">
                      <div className="font-serif text-lg truncate">{r.title}</div>
                      <div className="overline mt-0.5">{r.category}</div>
                    </div>
                  </div>
                  <a
                    href={downloadUrl(r.file_path)}
                    target="_blank"
                    rel="noreferrer"
                    className="mt-4 btn-outline-gold w-full"
                    data-testid={`resource-download-${r.id}`}
                  >
                    <Download size={14} /> {t("common.download")}
                  </a>
                </div>
              ))}
            </div>
          )}
        </section>
      )}
      {section === "onboarding" && (
        <section data-testid="section-onboarding">
          <div className="overline mb-3">{t("services.onboarding.overline")}</div>
          <h2 className="font-serif text-3xl mb-2">{t("services.onboarding.title")}</h2>
          <p className="text-neutral-400 text-sm mb-8 max-w-2xl">{t("services.onboarding.subtitle")}</p>
          {serviceOrders.length === 0 ? (
            <div className="text-neutral-500 text-sm">{t("services.onboarding.noOrders")}</div>
          ) : (
            <OnboardingForms orders={serviceOrders} onDone={reload} />
          )}
        </section>
      )}
      {section === "project" && (
        <section data-testid="section-project">
          <div className="overline mb-3">{t("services.project.overline")}</div>
          <h2 className="font-serif text-3xl mb-6">{t("services.project.title")}</h2>
          {serviceOrders.length === 0 ? (
            <div className="text-neutral-500 text-sm">{t("services.project.empty")}</div>
          ) : (
            <div className="space-y-4">
              {serviceOrders.map((o) => {
                const STAGES = ["pending", "in_design", "in_review", "delivered"];
                const idx = Math.max(0, STAGES.indexOf(o.project_status || "pending"));
                return (
                  <div key={o.id} className="card-imperial p-5" data-testid={`project-card-${o.id}`}>
                    <div className="flex flex-col md:flex-row md:items-center justify-between gap-3 mb-5">
                      <div>
                        <div className="overline">{o.package?.toUpperCase()} · ${o.amount}</div>
                        <div className="font-serif text-xl mt-1">{o.business_description ? o.business_description.slice(0, 80) : "Website creation order"}</div>
                        <div className="text-xs text-neutral-500 mt-1">
                          {o.payment_status === "paid" ? <span className="text-[#d4af37]">✓ paid</span> : o.payment_status}
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center gap-2 mt-4">
                      {STAGES.map((s, i) => (
                        <React.Fragment key={s}>
                          <div className="flex flex-col items-center flex-1 min-w-0">
                            <div className={`h-9 w-9 rounded-full flex items-center justify-center border-2 transition-colors ${
                              i < idx ? "bg-[#d4af37] border-[#d4af37] text-black"
                              : i === idx ? "bg-[#e6192b] border-[#e6192b] text-white"
                              : "border-[#222] text-neutral-600"
                            }`}>
                              {i < idx ? <Check size={14} /> : i + 1}
                            </div>
                            <div className={`mt-2 text-[11px] font-condensed tracking-widest uppercase text-center ${
                              i === idx ? "text-[#e6192b]" : i < idx ? "text-[#d4af37]" : "text-neutral-500"
                            }`}>
                              {t(`services.project.statuses.${s}`)}
                            </div>
                          </div>
                          {i < STAGES.length - 1 && (
                            <div className={`h-px flex-1 ${i < idx ? "bg-[#d4af37]" : "bg-[#222]"}`} />
                          )}
                        </React.Fragment>
                      ))}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </section>
      )}
    </DashboardLayout>
  );
}

function OnboardingForms({ orders, onDone }) {
  const { t } = useI18n();
  const [activeId, setActiveId] = React.useState(orders[0]?.id || null);
  React.useEffect(() => { if (!activeId && orders[0]?.id) setActiveId(orders[0].id); }, [orders, activeId]);
  const active = orders.find((o) => o.id === activeId) || orders[0];
  if (!active) return null;
  return (
    <div className="space-y-6">
      {orders.length > 1 && (
        <div className="flex flex-wrap gap-2" data-testid="onboarding-order-tabs">
          {orders.map((o) => (
            <button
              key={o.id}
              onClick={() => setActiveId(o.id)}
              data-testid={`onboarding-tab-${o.id}`}
              className={`px-3 py-2 text-xs font-condensed tracking-widest uppercase border transition-colors ${
                o.id === active.id
                  ? "border-[#d4af37] text-[#d4af37] bg-[#0d0d0d]"
                  : "border-[#1f1f1f] text-neutral-500 hover:text-white"
              }`}
            >
              {(o.package || "").toUpperCase()} · ${o.amount}
            </button>
          ))}
        </div>
      )}
      <OnboardingForm order={active} onDone={onDone} />
    </div>
  );
}

function OnboardingForm({ order, onDone }) {
  const { t } = useI18n();
  const [desc, setDesc] = React.useState(order.business_description || "");
  const [phone, setPhone] = React.useState(order.contact_phone || "");
  const [file, setFile] = React.useState(null);
  const [busy, setBusy] = React.useState(false);
  const submitted = order.onboarding_status === "submitted";

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!file) { toast.error(t("services.onboarding.errFile")); return; }
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("business_description", desc);
      fd.append("contact_phone", phone);
      fd.append("file", file);
      await api.post(`/services/${order.id}/onboarding`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(t("services.onboarding.success"));
      setFile(null);
      onDone && onDone();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    } finally { setBusy(false); }
  };

  return (
    <form onSubmit={onSubmit} className="card-imperial p-6 md:p-8 space-y-5" data-testid={`onboarding-form-${order.id}`}>
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-3 pb-5 border-b border-[#161616]">
        <div>
          <div className="overline">{(order.package || "").toUpperCase()} · ${order.amount}</div>
          <div className="font-serif text-xl mt-1">Order · {order.id.slice(0, 8)}</div>
        </div>
        {submitted && (
          <div className="inline-flex items-center gap-2 text-xs text-[#d4af37] font-condensed tracking-widest uppercase" data-testid="onboarding-submitted-badge">
            <Check size={14} /> {t("services.onboarding.submittedLabel")}
          </div>
        )}
      </div>

      {submitted && (
        <div className="text-sm text-neutral-400">{t("services.onboarding.submittedHint")}</div>
      )}

      <div>
        <div className="overline mb-1.5">{t("services.onboarding.logoLabel")} *</div>
        <label className="flex items-center justify-between gap-3 bg-[#0a0a0a] border border-[#1f1f1f] focus-within:border-[#d4af37] px-4 py-3 cursor-pointer transition-colors">
          <span className="text-sm text-neutral-300 truncate">
            {file ? file.name : (order.logo_filename || "—")}
          </span>
          <span className="btn-outline-gold text-xs">
            <Upload size={12} /> {t("common.upload")}
          </span>
          <input
            type="file"
            accept="image/*"
            className="hidden"
            onChange={(e) => setFile(e.target.files?.[0] || null)}
            data-testid="onboarding-logo-input"
          />
        </label>
        <div className="text-[11px] text-neutral-500 mt-1.5">{t("services.onboarding.logoHint")}</div>
      </div>

      <div>
        <div className="overline mb-1.5">{t("services.onboarding.descLabel")} *</div>
        <textarea
          required
          rows={6}
          value={desc}
          onChange={(e) => setDesc(e.target.value)}
          placeholder={t("services.onboarding.descPlaceholder")}
          data-testid="onboarding-desc-input"
          className="w-full bg-[#0a0a0a] border border-[#1f1f1f] focus:border-[#d4af37] outline-none px-4 py-3 text-sm text-neutral-100 placeholder-neutral-600 transition-colors resize-y"
        />
        <div className="text-[11px] text-neutral-500 mt-1.5">{t("services.onboarding.descHint")}</div>
      </div>

      <div>
        <div className="overline mb-1.5">{t("services.onboarding.phoneLabel")}</div>
        <input
          type="tel"
          value={phone}
          onChange={(e) => setPhone(e.target.value)}
          data-testid="onboarding-phone-input"
          className="w-full bg-[#0a0a0a] border border-[#1f1f1f] focus:border-[#d4af37] outline-none px-4 py-3 text-sm text-neutral-100 transition-colors"
        />
      </div>

      <button
        type="submit"
        disabled={busy}
        data-testid="onboarding-submit"
        className="btn-imperial"
      >
        {busy ? t("services.onboarding.sending") : (submitted ? t("services.onboarding.resubmit") : t("services.onboarding.submit"))}
      </button>
    </form>
  );
}
